import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Cpu, Zap, Play, Layers, Shield, Trash2, Code, Plus, 
  HelpCircle, RefreshCw, Terminal, Check, Info, Settings,
  Workflow, ArrowRight, Activity, Database
} from 'lucide-react';

interface CanvasNode {
  id: string;
  title: string;
  type: 'start' | 'guard' | 'agent' | 'ifelse' | 'transform';
  x: number;
  y: number;
  width: number;
  height: number;
}

interface NodeConnection {
  id: string;
  fromNodeId: string;
  toNodeId: string;
}

export function NexoraXMultiAgentNodeDeck() {
  // Religious Preamble Standard
  const PREAMBLE = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  // Pre-configured premium blueprint templates
  const BLUEPRINT_TEMPLATES = [
    {
      id: 'template-sentry',
      name: '🛡️ Security Sentry',
      description: 'Standard 3-node threat isolation & categorization sandbox pipeline.',
      nodes: [
        { id: 'node-start', title: '🟢 Start Trigger', type: 'start', x: 50, y: 180, width: 140, height: 50 },
        { id: 'node-guardrail', title: '🛡️ Jailbreak Guardrail', type: 'guard', x: 250, y: 150, width: 220, height: 50 },
        { id: 'node-classifier', title: '🧠 Classification Agent', type: 'agent', x: 540, y: 160, width: 220, height: 50 }
      ],
      connections: [
        { id: 'conn-1', fromNodeId: 'node-start', toNodeId: 'node-guardrail' },
        { id: 'conn-2', fromNodeId: 'node-guardrail', toNodeId: 'node-classifier' }
      ]
    },
    {
      id: 'template-fintech',
      name: '💸 Smart Levy Router',
      description: 'Extracts 1% 3% Community Allocation & 20% Tax, routing Net Settlements directly to Secure Payment Provider.',
      nodes: [
        { id: 'node-start', title: '🟢 Ingress Payment Flow', type: 'start', x: 40, y: 150, width: 170, height: 50 },
        { id: 'node-guardrail', title: '🛡️ Fraud Signature Pre-check', type: 'guard', x: 250, y: 110, width: 230, height: 50 },
        { id: 'node-tax-transform', title: '⚡ Compute Levy & 10% Maintenance & Hosting Fee', type: 'transform', x: 520, y: 135, width: 220, height: 50 },
        { id: 'node-SecurePayment-ledger', title: '🧠 Secure Payment Inflow Settlement Node', type: 'agent', x: 510, y: 250, width: 230, height: 50 }
      ],
      connections: [
        { id: 'conn-1', fromNodeId: 'node-start', toNodeId: 'node-guardrail' },
        { id: 'conn-2', fromNodeId: 'node-guardrail', toNodeId: 'node-tax-transform' },
        { id: 'conn-3', fromNodeId: 'node-tax-transform', toNodeId: 'node-SecurePayment-ledger' }
      ]
    },
    {
      id: 'template-strike',
      name: '🔥 CRM Strike Funnel',
      description: 'Detects high-value leads, transforms telemetry data, and issues WhatsApp strike pitches with Stripe checkouts.',
      nodes: [
        { id: 'node-start', title: '🟢 Lead Inbound Signal', type: 'start', x: 50, y: 180, width: 170, height: 50 },
        { id: 'node-decision', title: '🔀 Filter High-Value Deals', type: 'ifelse', x: 260, y: 230, width: 190, height: 50 },
        { id: 'node-strike-agent', title: '🧠 WhatsApp Strike Agent', type: 'agent', x: 500, y: 120, width: 220, height: 50 },
        { id: 'node-fail-logger', title: '🛡️ Audit Sandbox Logger', type: 'guard', x: 510, y: 310, width: 200, height: 50 }
      ],
      connections: [
        { id: 'conn-1', fromNodeId: 'node-start', toNodeId: 'node-decision' },
        { id: 'conn-2', fromNodeId: 'node-decision', toNodeId: 'node-strike-agent' },
        { id: 'conn-3', fromNodeId: 'node-decision', toNodeId: 'node-fail-logger' }
      ]
    },
    {
      id: 'template-swarm',
      name: '🧠 Deep Research Swarm',
      description: 'Orchestrates search clusters to monitor unauthorized domain duplicates.',
      nodes: [
        { id: 'node-start', title: '🟢 Swarm Init Socket', type: 'start', x: 40, y: 150, width: 160, height: 50 },
        { id: 'node-defense-filter', title: '🛡️ IP NexoraXty Filter', type: 'guard', x: 230, y: 110, width: 210, height: 50 },
        { id: 'node-lookup-transform', title: '⚡ DNS Telemetry Format', type: 'transform', x: 470, y: 130, width: 210, height: 50 },
        { id: 'node-oracle-agent', title: '🧠 The Oracle Strike Node', type: 'agent', x: 250, y: 240, width: 220, height: 50 }
      ],
      connections: [
        { id: 'conn-1', fromNodeId: 'node-start', toNodeId: 'node-defense-filter' },
        { id: 'conn-2', fromNodeId: 'node-defense-filter', toNodeId: 'node-lookup-transform' },
        { id: 'conn-3', fromNodeId: 'node-lookup-transform', toNodeId: 'node-oracle-agent' }
      ]
    }
  ];

  // Initial State setup following the precise requested sample nodes
  const [nodes, setNodes] = useState<CanvasNode[]>([
    { id: 'node-start', title: '🟢 Start Trigger', type: 'start', x: 50, y: 180, width: 140, height: 50 },
    { id: 'node-guardrail', title: '🛡️ Jailbreak Guardrail', type: 'guard', x: 250, y: 150, width: 220, height: 50 },
    { id: 'node-classifier', title: '🧠 Classification Agent', type: 'agent', x: 540, y: 160, width: 220, height: 50 }
  ]);

  const [connections, setConnections] = useState<NodeConnection[]>([
    { id: 'conn-1', fromNodeId: 'node-start', toNodeId: 'node-guardrail' },
    { id: 'conn-2', fromNodeId: 'node-guardrail', toNodeId: 'node-classifier' }
  ]);

  // Track active blueprint ID
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('template-sentry');

  // Port Connections & Interactive Selection State
  const [selectedPort, setSelectedPort] = useState<{ nodeId: string; portType: 'input' | 'output' } | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  // Dragging States for Canvas Node Relocation
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const nodeOffset = useRef({ x: 0, y: 0 });

  // Simulation parameters for dynamic signal pulse flow running
  const [isSimulating, setIsSimulating] = useState(false);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(-1);
  const [simulationLogs, setSimulationLogs] = useState<string[]>([]);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Active user guidelines configuration links
  const [activeNodeDetails, setActiveNodeDetails] = useState<string | null>(null);

  // Handle document level pointer up for clean drag release
  useEffect(() => {
    const handleMouseUpGlobal = () => {
      setDraggingNodeId(null);
    };

    window.addEventListener('mouseup', handleMouseUpGlobal);
    return () => window.removeEventListener('mouseup', handleMouseUpGlobal);
  }, []);

  // Handle responsive relative port calculations
  const getPortCoordinates = (node: CanvasNode, isOutput: boolean) => {
    if (isOutput) {
      return {
        x: node.x + node.width,
        y: node.y + node.height / 2
      };
    } else {
      return {
        x: node.x,
        y: node.y + node.height / 2
      };
    }
  };

  // Node Drag Handlers (Interactive Canvas Relocation)
  const handleNodeMouseDown = (e: React.MouseEvent, node: CanvasNode) => {
    // Avoid double trigger if clicking ports or custom utility buttons
    const target = e.target as HTMLElement;
    if (target.classList.contains('node-io-port') || target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }
    
    e.preventDefault();
    setSelectedNodeId(node.id);
    setSelectedConnectionId(null);
    setDraggingNodeId(node.id);
    
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      nodeOffset.current = {
        x: clickX - node.x,
        y: clickY - node.y
      };
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (!draggingNodeId || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    // Apply strict bounds within infinite canvas sandbox limits
    const newX = Math.max(10, Math.min(canvasRef.current.clientWidth - 230, currentX - nodeOffset.current.x));
    const newY = Math.max(10, Math.min(canvasRef.current.clientHeight - 60, currentY - nodeOffset.current.y));

    setNodes(prev => prev.map(node => {
      if (node.id === draggingNodeId) {
        return { ...node, x: newX, y: newY };
      }
      return node;
    }));
  };

  // Drag and Drop from Palette Handlers
  const handleDragStartPalette = (e: React.DragEvent, type: 'agent' | 'guard' | 'ifelse' | 'transform') => {
    e.dataTransfer.setData('text/plain', type);
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - 100; // center offset approx
    const y = e.clientY - rect.top - 25;

    const nodeType = e.dataTransfer.getData('text/plain') as 'agent' | 'guard' | 'ifelse' | 'transform';
    if (!nodeType) return;

    // Structure metadata based on type dropped
    let title = '🧠 Sub-Agent Node';
    let width = 220;
    if (nodeType === 'guard') {
      title = '🛡️ Security Guardrail';
    } else if (nodeType === 'ifelse') {
      title = '🔀 Decision IF/ELSE';
      width = 190;
    } else if (nodeType === 'transform') {
      title = '⚡ Payload Transform';
      width = 200;
    }

    const newNode: CanvasNode = {
      id: `node-${Date.now()}`,
      title,
      type: nodeType,
      x: Math.max(10, Math.min(canvasRef.current.clientWidth - width, x)),
      y: Math.max(10, Math.min(canvasRef.current.clientHeight - 60, y)),
      width,
      height: 50
    };

    setNodes(prev => [...prev, newNode]);
    setSimulationLogs(prev => [...prev, `[CANVAS] Spawned new ${nodeType.toUpperCase()} node on coordinates (${Math.round(x)}, ${Math.round(y)}).`]);
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Manage Port Interactive Handshake connections
  const handlePortClick = (nodeId: string, portType: 'input' | 'output', e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!selectedPort) {
      // First handshake click, prime the port
      setSelectedPort({ nodeId, portType });
      return;
    }

    // Secondary handshake click: complete or reset
    if (selectedPort.nodeId === nodeId) {
      // Cancelled if clicked on the same node
      setSelectedPort(null);
      return;
    }

    // Enforce directional logical validation: Output must bind with Input
    if (selectedPort.portType === 'output' && portType === 'input') {
      // Guard against duplicate structures
      const exists = connections.some(c => c.fromNodeId === selectedPort.nodeId && c.toNodeId === nodeId);
      if (!exists) {
        setConnections(prev => [...prev, {
          id: `conn-${Date.now()}`,
          fromNodeId: selectedPort.nodeId,
          toNodeId: nodeId
        }]);
        setSimulationLogs(prev => [...prev, `[ROUTING] Connected Output from node '${selectedPort.nodeId}' to Input at node '${nodeId}'.`]);
      }
    } else if (selectedPort.portType === 'input' && portType === 'output') {
      // Input clicked first then Output
      const exists = connections.some(c => c.fromNodeId === nodeId && c.toNodeId === selectedPort.nodeId);
      if (!exists) {
        setConnections(prev => [...prev, {
          id: `conn-${Date.now()}`,
          fromNodeId: nodeId,
          toNodeId: selectedPort.nodeId
        }]);
        setSimulationLogs(prev => [...prev, `[ROUTING] Connected Output from node '${nodeId}' to Input at node '${selectedPort.nodeId}'.`]);
      }
    }

    // Reset selection logic
    setSelectedPort(null);
  };

  // Execute Simulation through current route path coordinates
  const triggerSimulationFlow = () => {
    if (isSimulating) return;
    setIsSimulating(true);
    setActiveSegmentIndex(-1);
    setSimulationLogs(["[SYS-INIT] Channeling multi-agent node simulation framework..."]);

    const orderedConnections: NodeConnection[] = [];
    
    // Sort logic to trace pathways from Start recursively if possible, otherwise serial path
    let currentSrc = 'node-start';
    let stepsMax = 15;
    while (stepsMax > 0) {
      const nextConn = connections.find(c => c.fromNodeId === currentSrc);
      if (nextConn) {
        orderedConnections.push(nextConn);
        currentSrc = nextConn.toNodeId;
      } else {
        break;
      }
      stepsMax--;
    }

    if (orderedConnections.length === 0) {
      orderedConnections.push(...connections); // Fallback to raw collection
    }

    let currentStep = 0;
    const runStep = () => {
      if (currentStep >= orderedConnections.length) {
        setIsSimulating(false);
        setActiveSegmentIndex(-1);
        setSimulationLogs(prev => [
          ...prev,
          `[SUCCESS] Total pipeline execution resolved inside 0.8s standard limit.`,
          `[finance] Verified Levy deductions applied. Ledger mirroring complete to Google Drive NexoraX Resource Library.`
        ]);
        return;
      }

      const activeConn = orderedConnections[currentStep];
      const fromNode = nodes.find(n => n.id === activeConn.fromNodeId);
      const toNode = nodes.find(n => n.id === activeConn.toNodeId);
      
      setActiveSegmentIndex(connections.indexOf(activeConn));
      
      setSimulationLogs(prev => [
        ...prev,
        `[SIGNAL] Pulsing ledger vector: ${fromNode?.title || 'Unknown Source'} -> ${toNode?.title || 'Target Destination'}...`,
        `[COMPLIANCE] SLA Latency check: OK (<0.8s response target).`
      ]);

      currentStep++;
      setTimeout(runStep, 1500);
    };

    setTimeout(runStep, 1000);
  };

  // Deletion logic
  const handleNodeDelete = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (nodeId === 'node-start') {
      alert("NexoraX rules prohibit the termination of the root START node trigger.");
      return;
    }
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setConnections(prev => prev.filter(c => c.fromNodeId !== nodeId && c.toNodeId !== nodeId));
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
    setSimulationLogs(prev => [...prev, `[CLEANUP] Neutralized node and associated connections: ${nodeId}.`]);
  };

  const handleConnectionDelete = (connId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConnections(prev => prev.filter(c => c.id !== connId));
    if (selectedConnectionId === connId) setSelectedConnectionId(null);
    setSimulationLogs(prev => [...prev, `[CLEANUP] Severed routing connection: ${connId}.`]);
  };

  return (
    <div id="NexoraX-multi-agent-canvas-panel" className="bg-[#02050f] border border-cyan-500/20 rounded-3xl p-6 relative overflow-hidden shadow-[0_0_30px_rgba(6,182,212,0.05)] w-full max-w-7xl mx-auto space-y-6">
      
      {/* Visual Header Overlay */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#e840f3]/5 rounded-full blur-3xl pointer-events-none" />

      {/* Preamble standard banner */}
      <div className="text-center font-mono text-[9px] md:text-[10px] text-cyan-400 font-bold tracking-[0.25em] uppercase border-b border-white/5 pb-4">
        {PREAMBLE}
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start gap-4 pb-4 border-b border-slate-900">
        <div>
          <div className="text-cyan-400 font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
            <Workflow className="w-4 h-4 animate-spin text-[#e840f3]" style={{ animationDuration: '8s' }} />
            <span>NEXORAX NEXORAX PROTOCOL: AGENT ORCHESTRATION</span>
          </div>
          <h3 className="text-2xl font-black text-white tracking-tight uppercase mt-1">
            ADVANCED COGNITIVE MULTI-AGENT DESIGN NODE DECK
          </h3>
          <p className="text-slate-400 font-mono text-[10px] leading-relaxed uppercase mt-1 max-w-4xl">
            NexoraX blueprint node-sandbox: Drag components from the visual palette core, establish high-fidelity directional connection curves, and simulate live data signal pathways under Mahmud Director's 0.8s SLA standard.
          </p>
        </div>

        <div className="flex items-center gap-2 font-mono shrink-0">
          <button
            onClick={triggerSimulationFlow}
            disabled={isSimulating}
            className={`px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-400 hover:from-purple-400 hover:to-cyan-300 text-black font-extrabold rounded-xl text-[10px] uppercase tracking-widest flex items-center gap-1.5 shadow-[0_0_15px_rgba(6,182,212,0.2)] transition cursor-pointer ${
              isSimulating ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <Play className="w-3.5 h-3.5 fill-black text-black" />
            <span>{isSimulating ? 'SIMULATING FLOW...' : 'EXECUTE FLOW HANDSHAKE'}</span>
          </button>
          
          <button
            onClick={() => {
              setNodes([
                { id: 'node-start', title: '🟢 Start Trigger', type: 'start', x: 50, y: 180, width: 140, height: 50 },
                { id: 'node-guardrail', title: '🛡️ Jailbreak Guardrail', type: 'guard', x: 250, y: 150, width: 220, height: 50 },
                { id: 'node-classifier', title: '🧠 Classification Agent', type: 'agent', x: 540, y: 160, width: 220, height: 50 }
              ]);
              setConnections([
                { id: 'conn-1', fromNodeId: 'node-start', toNodeId: 'node-guardrail' },
                { id: 'conn-2', fromNodeId: 'node-guardrail', toNodeId: 'node-classifier' }
              ]);
              setSimulationLogs(['[SYS] Core canvas configurations reverted to standard deployment presets.']);
              setSelectedPort(null);
              setSelectedNodeId(null);
            }}
            className="px-3 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 rounded-xl text-[10px] uppercase tracking-widest transition cursor-pointer flex items-center gap-1"
            title="Reset preset nodes"
          >
            <RefreshCw className="w-3 h-3" />
            <span>RESET PRESETS</span>
          </button>
        </div>
      </div>

      {/* READY-MADE BLUEPRINT TEMPLATES ROW */}
      <div className="bg-[#03081e] border border-cyan-500/10 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between border-b border-white/5 pb-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#e840f3] animate-pulse" />
            <span className="text-[10px] text-white font-bold tracking-widest font-mono uppercase">
              SELECT READY-MADE BLUEPRINT TEMPLATE
            </span>
          </div>
          <span className="text-[8px] text-slate-500 font-mono font-bold uppercase">
            Click to load & customize any prebuilt architecture
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {BLUEPRINT_TEMPLATES.map((tpl) => {
            const isCurrent = selectedTemplateId === tpl.id;
            return (
              <button
                key={tpl.id}
                onClick={() => {
                  setSelectedTemplateId(tpl.id);
                  // Workaround deep copies to bypass state updates
                  setNodes(JSON.parse(JSON.stringify(tpl.nodes)));
                  setConnections(JSON.parse(JSON.stringify(tpl.connections)));
                  setSelectedPort(null);
                  setSelectedNodeId(null);
                  setSelectedConnectionId(null);
                  setSimulationLogs(prev => [
                    ...prev,
                    `[SYSTEM] Activated ready-made blueprint: '${tpl.name.toUpperCase()}'. Canvas populated with prebuilt configurations.`,
                    `[SYSTEM] Feel free to drag elements, add customized sub-agents, or connect/sever directional lines.`
                  ]);
                }}
                className={`text-left p-3.5 rounded-xl border font-mono transition-all duration-300 pointer-events-auto flex flex-col justify-between h-28 relative cursor-pointer group ${
                  isCurrent 
                    ? 'bg-purple-950/20 border-[#e840f3] shadow-[0_0_15px_rgba(232,64,243,0.15)]' 
                    : 'bg-slate-950/40 border-slate-900 hover:border-slate-800 hover:bg-slate-900/30'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-black uppercase tracking-wide ${
                      isCurrent ? 'text-white' : 'text-slate-300'
                    }`}>
                      {tpl.name}
                    </span>
                    {isCurrent && (
                      <span className="px-1.5 py-0.5 bg-[#e840f3]/10 text-[7px] text-[#e840f3] rounded border border-[#e840f3]/20 font-bold uppercase">
                        ACTIVE CODE
                      </span>
                    )}
                  </div>
                  <p className="text-[8px] text-slate-400 uppercase leading-relaxed mt-1.5 line-clamp-2">
                    {tpl.description}
                  </p>
                </div>

                <div className="flex items-center justify-between border-t border-white/5 pt-1.5 mt-1.5">
                  <span className="text-[7.5px] text-slate-500 uppercase">
                    Nodes: <strong className="text-slate-300">{tpl.nodes.length}</strong> | Connections: <strong className="text-slate-300">{tpl.connections.length}</strong>
                  </span>
                  <span className="text-[7.5px] text-cyan-400 font-bold group-hover:translate-x-1 transition-transform">
                    {isCurrent ? 'SELECTED' : 'LOAD ➔'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* CORE GRAPH WORKSPACE CONTAINER */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* DRAGGABLE PALETTE SIDEBAR (Col-span 3) */}
        <div className="lg:col-span-3 flex flex-col space-y-4 bg-black/60 border border-slate-900 rounded-3xl p-5 font-mono select-none">
          <div className="border-b border-slate-900 pb-2 mb-2">
            <span className="text-[10px] text-cyan-400 font-extrabold tracking-widest block uppercase">NODE PALETTE</span>
            <span className="text-[8px] text-slate-500 uppercase">DRAG BLOCKS TO ACTIVE CANVAS</span>
          </div>

          <div className="space-y-3">
            {[
              { type: 'agent', label: 'Agent Node', desc: 'NexoraX intelligence routine processor.', count: 'CORE', icon: <Cpu className="w-4 h-4 text-purple-400" /> },
              { type: 'guard', label: 'Guardrail Block', desc: 'Pre-checks inputs and outputs for security tokens.', count: 'PROTECT', icon: <Shield className="w-4 h-4 text-yellow-500" /> },
              { type: 'ifelse', label: 'If / else Branch', desc: 'Directs data based on validation constraints.', count: 'ROUTING', icon: <Workflow className="w-4 h-4 text-teal-400" /> },
              { type: 'transform', label: 'Payload Transform', desc: 'Converts records into designated structures.', count: 'DATA', icon: <Code className="w-4 h-4 text-blue-400" /> }
            ].map((palItem) => (
              <div
                key={palItem.type}
                draggable
                onDragStart={(e) => handleDragStartPalette(e, palItem.type as any)}
                className="p-3 bg-slate-950/80 border border-slate-900 rounded-xl cursor-grab hover:border-cyan-400/30 hover:bg-slate-950 transition active:cursor-grabbing group shadow-md"
              >
                <div className="flex items-center justify-between pointer-events-none pb-1.5 mb-1.5 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    {palItem.icon}
                    <span className="text-[10px] font-black text-white uppercase">{palItem.label}</span>
                  </div>
                  <span className="px-1.5 py-0.5 bg-white/5 text-[7px] text-slate-400 rounded-md font-bold">{palItem.count}</span>
                </div>
                <p className="text-[8.5px] text-slate-500 uppercase leading-relaxed pointer-events-none">
                  {palItem.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-[#00050c] p-3 rounded-xl border border-slate-950/80 text-[8px] text-slate-500 uppercase leading-relaxed flex items-center gap-1.5 select-none mt-auto">
            <Info className="w-4 h-4 text-cyan-400 shrink-0" />
            <span>Select nodes or connection pathways on the canvas to configure internal parameters or delete elements.</span>
          </div>
        </div>

        {/* DRAGGABLE CANVAS & CONNECTIONS CANVAS (Col-span 9) */}
        <div className="lg:col-span-9 flex flex-col space-y-4">
          <div className="bg-black/40 border border-slate-900 rounded-3xl overflow-hidden relative flex flex-col h-[460px]">
            
            {/* INFINITE CANVAS BODY */}
            <div
              ref={canvasRef}
              id="canvas-container"
              onMouseMove={handleCanvasMouseMove}
              onDragOver={handleCanvasDragOver}
              onDrop={handleCanvasDrop}
              className="flex-1 bg-gradient-to-br from-[#02050f] via-slate-950/50 to-[#02050f] relative overflow-hidden p-4 cursor-crosshair select-none"
              style={{
                backgroundImage: 'radial-gradient(ellipse 50% 50% at 50% 50%, #16223f 1px, transparent 1px)',
                backgroundSize: '24px 24px'
              }}
            >
              
              {/* LIVE SVG OVERLAY drawing beautiful curved connection paths */}
              <svg id="mcp-svg-line-canvas" className="absolute top-0 left-0 w-full h-full pointer-events-none z-10">
                <defs>
                  {/* Neon gradient definitions matching brand specifications */}
                  <linearGradient id="line-gradient-nx" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#a855f7" />
                    <stop offset="100%" stop-color="#38bdf8" />
                  </linearGradient>
                  
                  {/* Glowing execution highlight gradient */}
                  <linearGradient id="line-gradient-nx-pulse" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#e840f3" />
                    <stop offset="100%" stop-color="#4af183" />
                  </linearGradient>

                  {/* Marker for line direction arrowhead */}
                  <marker
                    id="arrow-head"
                    viewBox="0 0 10 10"
                    refX="6"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#475569" />
                  </marker>
                </defs>
                
                <g id="connections-group" className="pointer-events-auto">
                  {connections.map((conn, index) => {
                    const fromNode = nodes.find(n => n.id === conn.fromNodeId);
                    const toNode = nodes.find(n => n.id === conn.toNodeId);
                    
                    if (!fromNode || !toNode) return null;
                    
                    // Port coordinates (relative to the node placement coordinates)
                    const fromCoords = getPortCoordinates(fromNode, true);
                    const toCoords = getPortCoordinates(toNode, false);
                    
                    // Generate elegant bezier curves
                    const controlOffset = Math.abs(toCoords.x - fromCoords.x) * 0.5;
                    const curvePath = `M ${fromCoords.x} ${fromCoords.y} C ${fromCoords.x + controlOffset} ${fromCoords.y}, ${toCoords.x - controlOffset} ${toCoords.y}, ${toCoords.x} ${toCoords.y}`;
                    
                    const isSegmentActive = isSimulating && index === activeSegmentIndex;
                    const isSelected = selectedConnectionId === conn.id;

                    return (
                      <g key={conn.id} className="cursor-pointer group">
                        {/* Interactive fat collision path container */}
                        <path
                          d={curvePath}
                          fill="none"
                          stroke="transparent"
                          strokeWidth="20"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConnectionId(conn.id);
                            setSelectedNodeId(null);
                          }}
                        />

                        {/* Visual rendered pathway */}
                        <path
                          d={curvePath}
                          className="transition-all duration-300"
                          fill="none"
                          markerEnd="url(#arrow-head)"
                          stroke={
                            isSegmentActive 
                              ? 'url(#line-gradient-nx-pulse)' 
                              : isSelected
                                ? '#e840f3'
                                : 'url(#line-gradient-nx)'
                          }
                          strokeWidth={isSegmentActive || isSelected ? '4' : '2.5'}
                          style={{
                            filter: isSegmentActive 
                              ? 'drop-shadow(0px 0px 8px #e840f3)' 
                              : isSelected
                                ? 'drop-shadow(0px 0px 6px rgba(232, 64, 243, 0.5))'
                                : 'drop-shadow(0px 0px 3px rgba(56, 189, 248, 0.455))'
                          }}
                        />

                        {/* Interactive dynamic signal pulse circle moving along curves during active execution */}
                        {isSegmentActive && (
                          <circle r="6" fill="#e840f3">
                            <animateMotion
                              dur="1.5s"
                              repeatCount="indefinite"
                              path={curvePath}
                            />
                          </circle>
                        )}

                        {/* Small delete trigger at midpoint of selected curve */}
                        {isSelected && (
                          <foreignObject
                            x={(fromCoords.x + toCoords.x) / 2 - 12}
                            y={(fromCoords.y + toCoords.y) / 2 - 12}
                            width="24"
                            height="24"
                            className="z-50"
                          >
                            <button
                              type="button"
                              onClick={(e) => handleConnectionDelete(conn.id, e)}
                              className="w-6 h-6 rounded-full bg-red-600 hover:bg-red-500 border border-red-400 flex items-center justify-center text-white cursor-pointer shadow-lg"
                              title="Delete Connection"
                            >
                              <Trash2 size={11} className="stroke-[2.5px]" />
                            </button>
                          </foreignObject>
                        )}
                      </g>
                    );
                  })}
                </g>
              </svg>

              {/* RENDERED INTERACTIVE NODES */}
              {nodes.map((node) => {
                const isSelected = selectedNodeId === node.id;
                const hasInput = node.type !== 'start';
                const hasOutput = true; // All nodes support outputs

                // Calculate color schemes derived from node type
                let typeBorder = 'border-slate-800';
                let typeGlow = 'rgba(51, 65, 85, 0.1)';
                if (node.type === 'start') {
                  typeBorder = 'border-emerald-500/40';
                  typeGlow = 'rgba(16, 185, 129, 0.15)';
                } else if (node.type === 'guard') {
                  typeBorder = 'border-amber-500/40';
                  typeGlow = 'rgba(245, 158, 11, 0.15)';
                } else if (node.type === 'agent') {
                  typeBorder = 'border-purple-500/40';
                  typeGlow = 'rgba(168, 85, 247, 0.15)';
                } else if (node.type === 'ifelse') {
                  typeBorder = 'border-teal-500/40';
                  typeGlow = 'rgba(20, 184, 166, 0.15)';
                } else if (node.type === 'transform') {
                  typeBorder = 'border-blue-500/40';
                  typeGlow = 'rgba(59, 130, 246, 0.15)';
                }

                if (isSelected) {
                  typeBorder = 'border-[#e840f3] ring-1 ring-[#e840f3]/40';
                  typeGlow = 'rgba(232, 64, 243, 0.25)';
                }

                return (
                  <div
                    key={node.id}
                    className="absolute active-canvas-node transition-shadow duration-300 pointer-events-auto"
                    style={{
                      left: `${node.x}px`,
                      top: `${node.y}px`,
                      width: `${node.width}px`,
                      height: `${node.height}px`,
                      zIndex: isSelected ? 30 : 20
                    }}
                  >
                    {/* Node Main Body Box Container */}
                    <div
                      onMouseDown={(e) => handleNodeMouseDown(e, node)}
                      className={`w-full h-full bg-[#0d1527]/95 border ${typeBorder} rounded-xl px-4 flex items-center justify-between text-white font-mono text-[10px] uppercase font-bold tracking-wider relative cursor-grab active:cursor-grabbing select-none shadow-xl`}
                      style={{
                        boxShadow: `0 4px 15px rgba(0,0,0,0.5), inset 0 0 10px ${typeGlow}`
                      }}
                    >
                      {/* Left side node-input port toggle hook */}
                      {hasInput && (
                        <div
                          onClick={(e) => handlePortClick(node.id, 'input', e)}
                          className={`node-io-port input absolute -left-[5px] top-[21px] w-[10px] h-[10px] rounded-full border-2 border-[#02050f] transition-all hover:scale-125 z-40 cursor-pointer ${
                            selectedPort?.nodeId === node.id && selectedPort?.portType === 'input'
                              ? 'bg-[#e840f3] shadow-[0_0_8px_#e840f3]'
                              : 'bg-slate-400 hover:bg-cyan-400'
                          }`}
                          title="Input Port (Bind Output target here)"
                        />
                      )}

                      {/* Display title or core elements */}
                      <div className="flex items-center gap-1.5 truncate pr-2">
                        <span className="truncate">{node.title}</span>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {/* Selected individual Node trash bin deletion block */}
                        {isSelected && node.id !== 'node-start' && (
                          <button
                            type="button"
                            onClick={(e) => handleNodeDelete(node.id, e)}
                            className="p-1 hover:bg-white/5 text-slate-500 hover:text-red-400 rounded-md transition cursor-pointer"
                            title="Delete Node"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>

                      {/* Right side node-output port hook */}
                      {hasOutput && (
                        <div
                          onClick={(e) => handlePortClick(node.id, 'output', e)}
                          className={`node-io-port output absolute -right-[5px] top-[21px] w-[10px] h-[10px] rounded-full border-2 border-[#02050f] transition-all hover:scale-125 z-40 cursor-pointer ${
                            selectedPort?.nodeId === node.id && selectedPort?.portType === 'output'
                              ? 'bg-[#e840f3] shadow-[0_0_8px_#e840f3]'
                              : 'bg-slate-400 hover:bg-cyan-400'
                          }`}
                          title="Output Port (Drag signal pathway)"
                        />
                      )}
                    </div>
                  </div>
                );
              })}

              {/* EMPTY CANVAS PLACEHOLDER BACKGROUND TRACE */}
              {nodes.length <= 1 && (
                <div className="flex flex-col items-center justify-center h-full text-center text-slate-600 uppercase select-none space-y-2 pointer-events-none">
                  <Workflow className="w-12 h-12 text-slate-800 animate-pulse" />
                  <span className="text-[10px] font-bold tracking-widest text-slate-500">CANVAS EMPTY</span>
                  <span className="text-[8px] text-slate-700 max-w-xs">Drag modules from the sidebar to establish intelligence connections.</span>
                </div>
              )}
            </div>

            {/* QUICK STATUS TICKER AND ACTIVE ATTRIBUTES */}
            <div className="bg-black/90 border-t border-slate-900 p-3.5 flex items-center justify-between text-[8px] font-mono select-none uppercase tracking-wider text-slate-500 shrink-0">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><Activity size={12} className="text-cyan-400" /> TOTAL NODES SPANNING: <span className="text-slate-300 font-extrabold">{nodes.length}</span></span>
                <span className="flex items-center gap-1"><Database size={12} className="text-purple-400" /> TOTAL PATH LINKS: <span className="text-slate-300 font-extrabold">{connections.length}</span></span>
              </div>
              <div className="flex items-center gap-1 text-slate-600">
                <span>PORT INGRESS SECURITY:</span>
                <span className="text-emerald-400 font-bold px-1 py-0.5 bg-emerald-500/10 rounded border border-emerald-500/20 text-[7px]">NexoraX Port Locked</span>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* NODE CONFORMANCE LIVE DIAGNOSTICS & SYSTEM CONSOLE TIMELINE */}
      <div className="border border-slate-950 rounded-2xl bg-black/90 p-4 space-y-2.5">
        <div className="flex justify-between items-center text-[10px] font-mono border-b border-slate-900 pb-2">
          <span className="text-cyan-400 font-bold tracking-widest flex items-center gap-1.5 uppercase">
            <Terminal className="w-4 h-4 text-cyan-400" /> ACTIVE DECK SIMULATION LOG SHEET
          </span>
          <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">0.8S RESPONSE LATENCY DECODED</span>
        </div>

        <div className="space-y-1.5 text-[9px] uppercase leading-relaxed font-mono max-h-48 overflow-y-auto custom-scrollbar">
          {simulationLogs.length === 0 ? (
            <p className="text-slate-700 italic">[STANDBY] CLICK 'EXECUTE FLOW HANDSHAKE' TO PULSE SIGNAL PATHWAYS ACROSS MULTI-AGENT SEGMENTS.</p>
          ) : (
            simulationLogs.map((log, index) => (
              <p
                key={index}
                className={`tracking-tight ${
                  log.startsWith("[SUCCESS]") 
                    ? 'text-emerald-400 font-black' 
                    : log.startsWith('[ROUTING]')
                      ? 'text-blue-400'
                      : log.startsWith('[CLEANUP]')
                        ? 'text-rose-400'
                        : 'text-slate-300'
                }`}
              >
                {log}
              </p>
            ))
          )}
        </div>
      </div>

    </div>
  );
}
